/**
 * Text formatting utilities for chemical formulas and special characters
 */

const subscriptDigits: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
};

// Set of subscript digit characters for quick lookup
const subscriptDigitSet = new Set(Object.values(subscriptDigits));

/**
 * Check if a character is a subscript digit
 */
function isSubscriptDigit(char: string): boolean {
  return subscriptDigitSet.has(char);
}

/**
 * Converts numbers to subscripts based on chemical formula conventions.
 *
 * Rules:
 * - Numbers at the start or after non-letters remain as coefficients (normal digits)
 * - Numbers after letters, closing parentheses, or subscript digits become subscripts
 * - Consecutive digits after a letter all become subscripts
 *
 * Examples:
 * - "H2O" → "H₂O"
 * - "2H2O" → "2H₂O" (leading 2 stays normal, second 2 becomes subscript)
 * - "Ca(OH)2" → "Ca(OH)₂"
 * - "H2SO4" → "H₂SO₄"
 * - "C₁2" → "C₁₂" (number after subscript also becomes subscript)
 *
 * @param text - The input text to format
 * @returns The formatted text with subscripts
 */
export function convertNumbersToSubscripts(text: string): string {
  let result = '';
  let inSubscript = false; // Track if we're currently in a subscript sequence

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    // If current character is a digit
    if (/[0-9]/.test(char)) {
      // Check what comes before this digit
      const prevChar = i > 0 ? text[i - 1] : '';

      // If preceded by a letter, closing parenthesis, subscript digit, OR already in subscript mode, convert to subscript
      // Otherwise, keep as coefficient (normal digit)
      if (/[a-zA-Z)]/.test(prevChar) || isSubscriptDigit(prevChar) || inSubscript) {
        result += subscriptDigits[char] || char;
        inSubscript = true; // Enter subscript mode
      } else {
        result += char; // Keep as coefficient
      }
    } else {
      result += char;
      inSubscript = false; // Exit subscript mode when we hit a non-digit
    }
  }
  return result;
}
